import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import '@/components/ui/styles/customScrollBar.css';
import { useUserApplications } from '@/pages/user/hooks/useUserApplications';
import React, { useCallback, useEffect, useState } from 'react';
import COVEditForm from './permitForms/COVEditForm';
import CSAWEditForm from './permitForms/CSAWEditForm';
import PLTCPEditForm from './permitForms/PLTCPEditForm';
import PLTPEditForm from './permitForms/PLTPEditForm';
import PTPREditForm from './permitForms/PTPREditForm';
import TCEBPEditForm from './permitForms/TCEBPEditForm';

// Utility function to safely format date
const formatDate = (dateString) => {
   return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit'
   });
};

const EditDraftModal = ({ isOpen, onClose, onSave, application }) => {
   const [formData, setFormData] = useState(application);
   const { fetchCOVPermit, fetchCSAWPermit, fetchPLTCPPermit, fetchPTPRPermit, fetchPLTPPermit, fetchTCEBPPermit } = useUserApplications();
   const [hasFetched, setHasFetched] = useState(false);

   const fetchPermitData = useCallback(async () => {
      if (!hasFetched) {
         try {
            let permitData;
            switch (application.applicationType) {
               case 'Certificate of Verification':
                  permitData = await fetchCOVPermit(application.id);
                  break;
               case 'Chainsaw Registration':
                  permitData = await fetchCSAWPermit(application.id);
                  break;
               case 'Public Land Tree Cutting Permit':
                  permitData = await fetchPLTCPPermit(application.id);
                  break;
               case 'Private Tree Plantation Registration':
                  permitData = await fetchPTPRPermit(application.id);
                  break;
               case 'Private Land Timber Permit':
                  permitData = await fetchPLTPPermit(application.id);
                  break;
               case 'Tree Cutting and/or Earth Balling Permit':
                  permitData = await fetchTCEBPPermit(application.id);
                  break;
               default:
                  permitData = application;
            }
            console.log('Permit data fetched:', permitData);
            setFormData(permitData);
            setHasFetched(true);
         } catch (error) {
            console.error('Error fetching permit data:', error);
         }
      }
   }, [application, fetchCOVPermit, fetchCSAWPermit, fetchPLTCPPermit, fetchPTPRPermit, fetchPLTPPermit, fetchTCEBPPermit, hasFetched]);

   useEffect(() => {
      if (isOpen) {
         fetchPermitData();
         setHasFetched(true); // stop fetching after first fetch
      } else {
         setHasFetched(false);
      }
   }, [isOpen, fetchPermitData]);

   const handleInputChange = (e) => {
      const { name, value } = e.target;
      console.log('handleInputChange called with:', { name, value });

      // Handle complex form updates (like formUpdate)
      if (name === 'formUpdate' && typeof value === 'object') {
         console.log('Handling formUpdate with:', value);
         setFormData(prevData => {
            const newData = {
               ...prevData,
               ...value
            };
            console.log('Updated form data:', newData);
            return newData;
         });
         return;
      }

      // Handle request type changes
      if (name === 'requestType') {
         console.log('Handling request type change:', value);
         setFormData(prevData => {
            const newData = {
               ...prevData,
               requestType: value,
               isRequestForCutting: value === 'Cutting',
               isRequestForEarthBalling: value === 'Earth Balling',
               // Reset inventory purpose when changing request type
               inventoryPurpose: value === 'Inventory' ? prevData.inventoryPurpose : undefined
            };
            console.log('Updated form data after request type change:', newData);
            return newData;
         });
         return;
      }

      // Handle regular input changes
      setFormData(prevData => {
         const newData = {
            ...prevData,
            [name]: value
         };
         console.log('Updated form data after regular change:', newData);
         return newData;
      });
   };

   const handleCheckboxChange = (name, checked) => {
      setFormData(prevData => ({
         ...prevData,
         [name]: checked
      }));
   };

   const readFileAsBase64 = (file) => {
      return new Promise((resolve, reject) => {
         const reader = new FileReader();
         reader.onload = () => resolve(reader.result.split(',')[1]);
         reader.onerror = (error) => reject(error);
         reader.readAsDataURL(file);
      });
   };

   const handleFileChange = async (e, documentType) => {
      const file = e.target.files[0];
      if (file) {
         try {
            const content = await readFileAsBase64(file);
            setFormData(prevData => ({
               ...prevData,
               files: {
                  ...(prevData.files || {}),  // Use empty object if files is null or undefined
                  [documentType]: [
                     ...(prevData.files?.[documentType] || []),
                     {
                        filename: file.name,
                        contentType: file.type,
                        data: content
                     }
                  ]
               }
            }));
         } catch (error) {
            console.error('Error processing file:', error);
            // Handle the error (e.g., show a toast notification)
         }
      }
   };

   const removeFile = (documentType, index) => {
      setFormData(prevData => {
         const updatedFiles = { ...prevData.files };
         if (updatedFiles[documentType]) {
            updatedFiles[documentType] = updatedFiles[documentType].filter((_, i) => i !== index);
         }
         return { ...prevData, files: updatedFiles };
      });
   };

   const removeAllFiles = (documentType) => {
      setFormData(prevData => {
         const updatedFiles = { ...prevData.files };
         delete updatedFiles[documentType];
         return { ...prevData, files: updatedFiles };
      });
   };

   const handleSubmit = (e) => {
      e.preventDefault();
      console.log('Submitting form data:', formData);
      // Ensure applicationType is included in the data being saved
      const dataToSave = {
         ...formData,
         applicationType: application.applicationType,
         status: application.status === 'Returned' ? 'Submitted' : formData.status, // Change status to 'Submitted' if it was 'Returned'
         // Ensure inventory-related fields are included
         isRequestForCutting: formData.isRequestForCutting,
         isRequestForEarthBalling: formData.isRequestForEarthBalling,
         inventoryPurpose: formData.inventoryPurpose
      };
      console.log('Saving data:', dataToSave);
      onSave(dataToSave);
      onClose();
   };

   const renderFormByType = () => {
      switch (application.applicationType) {
         case 'Certificate of Verification':
            return <COVEditForm
               formData={formData}
               handleInputChange={handleInputChange}
               handleFileChange={handleFileChange}
               removeFile={removeFile}
            />;
         case 'Chainsaw Registration':
            return <CSAWEditForm
               formData={formData}
               handleInputChange={handleInputChange}
               handleFileChange={handleFileChange}
               removeFile={removeFile}
               handleCheckboxChange={handleCheckboxChange}
            />;
         case 'Public Land Tree Cutting Permit':
            return <PLTCPEditForm
               formData={formData}
               handleInputChange={handleInputChange}
               handleFileChange={handleFileChange}
               removeFile={removeFile}
               handleCheckboxChange={handleCheckboxChange}
            />;
         case 'Private Tree Plantation Registration':
            return <PTPREditForm
               formData={formData}
               handleInputChange={handleInputChange}
               handleFileChange={handleFileChange}
               removeFile={removeFile}
            />;
         case 'Private Land Timber Permit':
            return <PLTPEditForm
               formData={formData}
               handleInputChange={handleInputChange}
               handleFileChange={handleFileChange}
               removeFile={removeFile}
               handleCheckboxChange={handleCheckboxChange}
            />;
         case 'Tree Cutting and/or Earth Balling Permit':
            return <TCEBPEditForm
               formData={formData}
               handleInputChange={handleInputChange}
               handleFileChange={handleFileChange}
               removeFile={removeFile}
            />;
         default:
            return <p>Unsupported permit type: {application.applicationType}</p>;
      }
   };

   return (
      <Dialog open={isOpen} onOpenChange={onClose}>
         <DialogContent className="sm:max-w-[600px] max-h-[90vh] flex flex-col">
            <DialogHeader>
               <DialogTitle>Edit Draft Application</DialogTitle>
               <DialogDescription>
                  Edit the details of this draft application.
               </DialogDescription>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="flex-grow overflow-y-auto custom-scrollbar">
               <div>
                  {renderFormByType()}
               </div>
            </form>
            <DialogFooter className="mt-4">
               <Button type="button" onClick={onClose} variant="outline">Cancel</Button>
               <Button type="submit" onClick={handleSubmit}>Save changes</Button>
            </DialogFooter>
         </DialogContent>
      </Dialog>
   );
};

export default EditDraftModal;